import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { axiosInstance } from "@/lib/axios-instance";
import { cn, FILE_TYPE_MAPPING } from "@/lib/utils";
import { getErrorMessage } from "@/lib/api-error";
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  Copy,
  File,
  FileImage,
  FileSpreadsheet,
  FileText,
  Link,
  Loader2,
  Presentation,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";
import {
  extractGoogleDriveFileId,
  googleTokenHeader,
} from "@/lib/google-drive";
import {
  DRIVE_FILE_SCOPE,
  openDrivePicker,
  type PickedFile,
} from "@/lib/google-picker";

/*
 * Google Drive import.
 *
 * This used to be a hand-built browse UI — search, breadcrumbs, folder
 * expansion — backed by our own /google-drive/list-files. Listing someone's
 * Drive needs `drive.readonly`, a Google *restricted* scope, and an app that
 * requests one is blocked with "Google hasn't verified this app" for every user
 * outside the test-user list until it clears app verification plus an annual
 * CASA security assessment. That is the "source not trusted" error the client
 * reported, and no amount of application code fixes it.
 *
 * So the browse UI is gone and Google's own Picker takes its place. The listing
 * now happens inside Google's iframe under the user's session, which means we
 * only need `drive.file` — non-sensitive, no verification, no user cap, and
 * access limited to exactly the files the user picked.
 *
 * What that costs: no cross-Drive search from inside our app, and no
 * "import this whole folder tree" in one action. The Picker's own multi-select
 * covers the common case.
 */

const GoogleDriveIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
      fill="#00ac47"
    />
    <path
      d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
      fill="#ea4335"
    />
    <path
      d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

interface ImportedFile {
  _id: string;
  file_type: string;
  file_url?: string;
  file_name?: string;
}

interface GoogleDrivePickerProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  opportunityId: string;
  onImportComplete?: (files: ImportedFile[]) => void;
  onImportError?: (failedCount: number) => void;
  onSelectionConfirm?: (files: PickedFile[], accessToken: string) => void;
  selectedLabel?: string;
  mode?: "import" | "select";
}

const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25MB

const FILE_TYPE_OPTIONS = Object.entries(FILE_TYPE_MAPPING).map(
  ([label, value]) => ({ label, value })
);

const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
/** Cloud project *number*. A wrong value here 404s the later backend fetch. */
const APP_ID = import.meta.env.VITE_GOOGLE_APP_ID;

const isGoogleDoc = (mimeType: string) =>
  mimeType.startsWith("application/vnd.google-apps.") &&
  mimeType !== "application/vnd.google-apps.folder";

const isValidFileType = (mimeType: string) => {
  const valid = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "image/jpeg",
    "image/png",
    "image/gif",
    "text/plain",
    "text/csv",
  ];
  if (isGoogleDoc(mimeType)) return true;
  return valid.includes(mimeType) || mimeType.startsWith("image/");
};

const isFileSizeValid = (size?: number) =>
  size === undefined || size <= MAX_FILE_SIZE_BYTES;

const formatSize = (bytes?: number, mimeType?: string) => {
  if (mimeType && isGoogleDoc(mimeType)) {
    if (mimeType.includes("document")) return "Google Doc";
    if (mimeType.includes("spreadsheet")) return "Google Sheet";
    if (mimeType.includes("presentation")) return "Google Slide";
    return "Google File";
  }
  if (!bytes) return "-";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const getFileIcon = (mimeType: string) => {
  if (mimeType === "application/pdf")
    return <FileText className="h-5 w-5 flex-shrink-0 text-red-500" />;
  if (mimeType.includes("word") || mimeType.includes("document"))
    return <FileText className="h-5 w-5 flex-shrink-0 text-blue-600" />;
  if (
    mimeType.includes("excel") ||
    mimeType.includes("spreadsheet") ||
    mimeType === "text/csv"
  )
    return <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-green-600" />;
  if (mimeType.includes("powerpoint") || mimeType.includes("presentation"))
    return <Presentation className="h-5 w-5 flex-shrink-0 text-orange-500" />;
  if (mimeType.startsWith("image/"))
    return <FileImage className="h-5 w-5 flex-shrink-0 text-purple-500" />;
  return <File className="h-5 w-5 flex-shrink-0 text-gray-500" />;
};

export function GoogleDrivePicker({
  open,
  onClose,
  companyId,
  opportunityId,
  onImportComplete,
  onImportError,
  onSelectionConfirm,
  mode = "import",
}: GoogleDrivePickerProps) {
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [fileTypes, setFileTypes] = useState<Record<string, string>>({});
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [opening, setOpening] = useState(false);
  const [typePopoverFor, setTypePopoverFor] = useState<string | null>(null);

  // Paste-a-link path, which goes through the service account rather than the
  // user's own grant and is kept as-is.
  const [driveLink, setDriveLink] = useState("");
  const [isImportingLink, setIsImportingLink] = useState(false);
  const [linkAccessError, setLinkAccessError] = useState<{
    message: string;
    fileId: string;
    serviceAccountEmail?: string;
  } | null>(null);
  const [pasteLinkFileType, setPasteLinkFileType] = useState("");
  const [pasteLinkTypeOpen, setPasteLinkTypeOpen] = useState(false);

  const reset = () => {
    setPicked([]);
    setFileTypes({});
    setDriveLink("");
    setLinkAccessError(null);
    setPasteLinkFileType("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const showPicker = async (token: string) => {
    setAccessToken(token);
    if (!API_KEY || !APP_ID) {
      toast.error(
        "Google Picker is not configured. Set VITE_GOOGLE_API_KEY and VITE_GOOGLE_APP_ID."
      );
      return;
    }
    setOpening(true);
    try {
      const files = await openDrivePicker({
        token,
        apiKey: API_KEY,
        appId: APP_ID,
        multiselect: true,
      });
      const usable = files.filter(
        (f) => !f.isFolder && isValidFileType(f.mimeType) && isFileSizeValid(f.size)
      );
      const skipped = files.length - usable.length;

      setPicked((prev) => {
        const seen = new Set(prev.map((f) => f.id));
        return [...prev, ...usable.filter((f) => !seen.has(f.id))];
      });

      if (usable.length === 0 && files.length > 0) {
        toast.info(
          "Those files can't be imported — PDFs, docs, spreadsheets, presentations and images under 25MB only."
        );
      } else if (skipped > 0) {
        toast.info(`Added ${usable.length}; skipped ${skipped} (type or size).`);
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Couldn't open Google Drive"));
    } finally {
      setOpening(false);
    }
  };

  // `drive.file` only — see the note at the top of this file. Requesting the
  // narrower scope is the entire reason the consent screen stops warning.
  const login = useGoogleLogin({
    scope: DRIVE_FILE_SCOPE,
    onSuccess: (response) => showPicker(response.access_token),
    onError: () => toast.error("Google sign-in was cancelled or failed"),
  });

  const removePicked = (id: string) => {
    setPicked((prev) => prev.filter((f) => f.id !== id));
    // Drop the type with the file so the two can't drift apart.
    setFileTypes((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const allTyped = picked.every((f) => fileTypes[f.id]);
  const canSubmit =
    picked.length > 0 && (mode === "select" || allTyped) && !importing;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    if (mode === "select") {
      onSelectionConfirm?.(
        picked.map((f) => ({ ...f, selectedType: fileTypes[f.id] })) as PickedFile[],
        accessToken || ""
      );
      handleClose();
      return;
    }

    setImporting(true);
    const results: ImportedFile[] = [];
    let failed = 0;

    try {
      for (const file of picked) {
        try {
          const response = await axiosInstance.post(
            "/google-drive/import-file",
            {
              company_id: companyId,
              opportunity_id: opportunityId,
              file_id: file.id,
              file_type: fileTypes[file.id],
            },
            { headers: googleTokenHeader(accessToken) }
          );
          if (response.data.status === "success") results.push(response.data.file);
          else failed++;
        } catch (err) {
          console.error(`Failed to import ${file.id}:`, err);
          failed++;
        }
      }

      if (results.length > 0) onImportComplete?.(results);
      if (failed > 0) {
        onImportError?.(failed);
        toast.error(`Failed to import ${failed} file${failed === 1 ? "" : "s"}`);
      }
      if (results.length > 0) {
        toast.success(
          `Imported ${results.length} file${results.length === 1 ? "" : "s"}`
        );
        handleClose();
      }
    } finally {
      setImporting(false);
    }
  };

  const handleLinkSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLinkAccessError(null);

    const fileId = extractGoogleDriveFileId(driveLink);
    if (!fileId) {
      toast.error("That doesn't look like a Google Drive link.");
      return;
    }
    if (mode === "import" && !pasteLinkFileType) {
      toast.error("Select a document type");
      return;
    }

    setIsImportingLink(true);
    try {
      if (mode === "select") {
        const response = await axiosInstance.get("/google-drive/file-info", {
          params: { file_id: fileId },
        });
        if (response.data.status !== "success") {
          throw new Error(response.data.message || "Failed to read that file");
        }
        const info = response.data.file;
        toast.success(`Added "${info.name}"`);
        setDriveLink("");
        onSelectionConfirm?.([{ ...info, isFolder: false }], "service_account");
        handleClose();
        return;
      }

      const response = await axiosInstance.post("/google-drive/import-file", {
        company_id: companyId,
        opportunity_id: opportunityId,
        file_id: fileId,
        file_type: pasteLinkFileType,
      });
      if (response.data.status !== "success") {
        throw new Error(response.data.message || "Import failed");
      }

      const label =
        FILE_TYPE_OPTIONS.find((o) => o.value === pasteLinkFileType)?.label ??
        pasteLinkFileType;
      const ext = response.data.file?.file_name?.split(".").pop() || "pdf";
      onImportComplete?.([
        { ...response.data.file, file_name: `${label}.${ext}` },
      ]);
      toast.success("File imported from Google Drive");
      handleClose();
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: Record<string, string>; status?: number };
        message?: string;
      };
      const data = error.response?.data;
      const message = data?.message || error.message || "";
      const isAccessError =
        data?.code === "service_account_no_access" ||
        error.response?.status === 403 ||
        error.response?.status === 404 ||
        message.includes("not accessible") ||
        message.includes("not found");

      if (isAccessError) {
        // The old copy said "request access from the file owner", but the owner
        // is usually the user themselves — the actual remedy is to connect
        // their account or share with the service account.
        setLinkAccessError({
          message:
            message ||
            "We can't open that file. Connect your Google account above, or share it with our service account.",
          fileId,
          serviceAccountEmail: data?.service_account_email,
        });
      } else {
        toast.error(message || "Failed to import that link.");
      }
    } finally {
      setIsImportingLink(false);
    }
  };

  return (
    // Google's Picker is appended to <body>, outside this dialog. A modal Radix
    // dialog sets `pointer-events: none` on <body> and traps focus, so the
    // Picker drew on top but ignored every click — nothing could be selected.
    // While it is up, drop to non-modal and don't treat clicks in it as a
    // reason to close.
    <Dialog open={open} onOpenChange={handleClose} modal={!opening}>
      <DialogContent
        className="flex h-[85vh] max-w-3xl flex-col"
        onInteractOutside={(event) => {
          if (opening) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GoogleDriveIcon className="h-5 w-5" />
            {mode === "select"
              ? "Select from Google Drive"
              : "Import from Google Drive"}
          </DialogTitle>
          <DialogDescription>
            Choose files in Google's picker. We only get access to what you pick.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto">
          <Button
            onClick={() => login()}
            variant="outline"
            className="h-12 w-full gap-2"
            disabled={opening || importing}
          >
            {opening ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <GoogleDriveIcon className="h-5 w-5" />
            )}
            {picked.length > 0
              ? "Choose more files"
              : "Choose files from Google Drive"}
          </Button>

          {picked.length > 0 && (
            <div className="rounded-lg border">
              <div className="border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                {picked.length} selected
              </div>
              <ScrollArea className="max-h-[38vh]">
                <div className="divide-y">
                  {picked.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-3 p-3"
                    >
                      {getFileIcon(file.mimeType)}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {file.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatSize(file.size, file.mimeType)}
                        </p>
                      </div>

                      {mode !== "select" && (
                        <Popover
                          open={typePopoverFor === file.id}
                          onOpenChange={(o) =>
                            setTypePopoverFor(o ? file.id : null)
                          }
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-invalid={!fileTypes[file.id]}
                              className={cn(
                                "h-8 w-[170px] justify-between text-xs font-normal",
                                !fileTypes[file.id] && "border-destructive/60"
                              )}
                            >
                              <span className="truncate">
                                {FILE_TYPE_OPTIONS.find(
                                  (o) => o.value === fileTypes[file.id]
                                )?.label ?? "Document type"}
                              </span>
                              <ChevronsUpDown className="ml-1 h-3 w-3 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[220px] p-0" align="end">
                            <Command>
                              <CommandInput placeholder="Search type..." />
                              <CommandList>
                                <CommandEmpty>No type found.</CommandEmpty>
                                <CommandGroup>
                                  {FILE_TYPE_OPTIONS.map((option) => (
                                    <CommandItem
                                      key={option.value}
                                      value={option.label}
                                      onSelect={() => {
                                        setFileTypes((prev) => ({
                                          ...prev,
                                          [file.id]: option.value,
                                        }));
                                        setTypePopoverFor(null);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-3 w-3",
                                          fileTypes[file.id] === option.value
                                            ? "opacity-100"
                                            : "opacity-0"
                                        )}
                                      />
                                      {option.label}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}

                      <button
                        type="button"
                        onClick={() => removePicked(file.id)}
                        aria-label={`Remove ${file.name}`}
                        className="text-muted-foreground/60 hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <div className="relative flex items-center py-1">
            <div className="flex-grow border-t border-border" />
            <span className="mx-4 flex-shrink-0 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              or paste a link
            </span>
            <div className="flex-grow border-t border-border" />
          </div>

          <div className="space-y-2">
            <form onSubmit={handleLinkSubmit} className="flex gap-2">
              <Input
                placeholder="https://docs.google.com/document/d/..."
                value={driveLink}
                onChange={(e) => {
                  setDriveLink(e.target.value);
                  if (linkAccessError) setLinkAccessError(null);
                }}
                disabled={isImportingLink}
                className={cn(linkAccessError && "border-red-300")}
              />
              <Button
                type="submit"
                disabled={
                  !driveLink.trim() ||
                  isImportingLink ||
                  (mode === "import" && !pasteLinkFileType)
                }
                className="shrink-0"
              >
                {isImportingLink ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Link className="h-4 w-4" />
                )}
                <span className="sr-only">Import link</span>
              </Button>
            </form>

            {mode === "import" && (
              <Popover
                open={pasteLinkTypeOpen}
                onOpenChange={setPasteLinkTypeOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                    disabled={isImportingLink}
                  >
                    {FILE_TYPE_OPTIONS.find(
                      (o) => o.value === pasteLinkFileType
                    )?.label ?? "Select document type..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command>
                    <CommandInput placeholder="Search document type..." />
                    <CommandList>
                      <CommandEmpty>No document type found.</CommandEmpty>
                      <CommandGroup>
                        {FILE_TYPE_OPTIONS.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.label}
                            onSelect={() => {
                              setPasteLinkFileType(option.value);
                              setPasteLinkTypeOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                pasteLinkFileType === option.value
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}

            {linkAccessError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                <div className="flex-1">
                  <p className="text-red-700">{linkAccessError.message}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {linkAccessError.serviceAccountEmail && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            linkAccessError.serviceAccountEmail!
                          );
                          toast.success("Service account email copied");
                        }}
                      >
                        <Copy className="mr-1.5 h-3 w-3" />
                        Copy service account email
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open(
                          `https://drive.google.com/file/d/${linkAccessError.fileId}/view`,
                          "_blank"
                        )
                      }
                    >
                      Open in Drive to share
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2 sm:justify-between">
          <div className="text-xs text-muted-foreground">
            {mode !== "select" && picked.length > 0 && !allTyped && (
              <span className="text-destructive">
                Assign a document type to every file
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={importing}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!canSubmit}>
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "select"
                ? `Add ${picked.length || ""}`.trim()
                : `Import ${picked.length || ""}`.trim()}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GoogleDrivePicker;
